/** Client-side LEAP birth-range preview / validation (mirrors engine rules). */

export type CountryCode = '01' | '02' | '03'

const PREFIX = 'LEAP'
const RFID_LENGTH = 20

const SERIAL_RANGES: Record<CountryCode, { min: number; max: number }> = {
  '01': { min: 1_000_000_001, max: 1_999_999_999 },
  '02': { min: 2_000_000_001, max: 2_999_999_999 },
  '03': { min: 3_000_000_001, max: 3_999_999_999 },
}

export const COUNTRY_OPTIONS: Array<{ code: CountryCode; label: string }> = [
  { code: '01', label: 'IN' },
  { code: '02', label: 'UAE' },
  { code: '03', label: 'KSA' },
]

export function countryLabel(code: CountryCode): string {
  return COUNTRY_OPTIONS.find((c) => c.code === code)?.label ?? code
}

export function isCountryCode(value: string): value is CountryCode {
  return value === '01' || value === '02' || value === '03'
}

function isTenDigitSerial(raw: string): boolean {
  return /^\d{10}$/.test(raw.trim())
}

function padSerial(n: number): string {
  return String(n).padStart(10, '0')
}

function buildRfidCode(country: CountryCode, monthYear: string, serial: string): string {
  return `${PREFIX}${country}${monthYear}${serial}`
}

function parseLeap(raw: string):
  | { ok: true; country_code: CountryCode; month_year: string; serial: string; serialNum: number }
  | { ok: false; error: string } {
  const cleaned = raw.trim().toUpperCase().replace(/[\s:.-]/g, '')
  if (cleaned.length !== RFID_LENGTH || !cleaned.startsWith(PREFIX)) {
    return { ok: false, error: 'Enter a full LEAP code (20 chars) or a 10-digit serial' }
  }
  const country_code = cleaned.slice(4, 6)
  if (!isCountryCode(country_code)) {
    return { ok: false, error: 'country_code must be 01, 02, or 03' }
  }
  const month_year = cleaned.slice(6, 10)
  if (!/^\d{4}$/.test(month_year)) {
    return { ok: false, error: 'month_year must be MMYY' }
  }
  const month = Number(month_year.slice(0, 2))
  if (month < 1 || month > 12) {
    return { ok: false, error: 'month in MMYY must be 01–12' }
  }
  const serial = cleaned.slice(10, 20)
  if (!/^\d{10}$/.test(serial)) {
    return { ok: false, error: 'serial must be 10 digits' }
  }
  const serialNum = Number(serial)
  const range = SERIAL_RANGES[country_code]
  if (serialNum < range.min || serialNum > range.max) {
    return { ok: false, error: `serial out of range for country ${country_code}` }
  }
  return { ok: true, country_code, month_year, serial, serialNum }
}

function parseSerial(
  raw: string,
  country_code: CountryCode
): { ok: true; serial: string; serialNum: number } | { ok: false; error: string } {
  const serial = raw.trim()
  if (!/^\d{10}$/.test(serial)) {
    return { ok: false, error: 'serial must be 10 digits' }
  }
  const serialNum = Number(serial)
  const range = SERIAL_RANGES[country_code]
  if (serialNum < range.min || serialNum > range.max) {
    return { ok: false, error: `serial out of range for country ${country_code}` }
  }
  return { ok: true, serial, serialNum }
}

export type BirthRangePreview = {
  country_code: CountryCode
  month_year: string
  start_serial: string
  end_serial: string
  start_rfid_code: string
  end_rfid_code: string
  expected_count: number
  needsCountryMonth: boolean
}

export type BirthRangePreviewResult =
  | { valid: true; preview: BirthRangePreview }
  | { valid: false; error: string }

export function previewBirthRange(input: {
  start: string
  end: string
  country_code: string
  month_year: string
}): BirthRangePreviewResult {
  const startRaw = input.start.trim()
  const endRaw = input.end.trim()
  if (!startRaw || !endRaw) {
    return { valid: false, error: 'Enter start and end of the series' }
  }

  let country_code: CountryCode | null = null
  let month_year: string | null = null
  let start_serial = ''
  let end_serial = ''
  let start_serial_num = 0
  let end_serial_num = 0
  let needsCountryMonth = false

  if (!isTenDigitSerial(startRaw)) {
    const p = parseLeap(startRaw)
    if (!p.ok) return { valid: false, error: `Start: ${p.error}` }
    country_code = p.country_code
    month_year = p.month_year
    start_serial = p.serial
    start_serial_num = p.serialNum
  } else {
    needsCountryMonth = true
  }

  if (!isTenDigitSerial(endRaw)) {
    const p = parseLeap(endRaw)
    if (!p.ok) return { valid: false, error: `End: ${p.error}` }
    if (country_code && country_code !== p.country_code) {
      return { valid: false, error: 'Start and end country must match' }
    }
    if (month_year && month_year !== p.month_year) {
      return { valid: false, error: 'Start and end month/year (MMYY) must match' }
    }
    country_code = country_code ?? p.country_code
    month_year = month_year ?? p.month_year
    end_serial = p.serial
    end_serial_num = p.serialNum
  } else {
    needsCountryMonth = true
  }

  if (!country_code) {
    if (!isCountryCode(input.country_code)) {
      return { valid: false, error: 'Select a country' }
    }
    country_code = input.country_code
  }

  if (!month_year) {
    const my = input.month_year.trim()
    if (!/^\d{4}$/.test(my)) {
      return { valid: false, error: 'Enter month/year as MMYY' }
    }
    const month = Number(my.slice(0, 2))
    if (month < 1 || month > 12) {
      return { valid: false, error: 'Month in MMYY must be 01–12' }
    }
    month_year = my
  }

  if (!start_serial) {
    const s = parseSerial(startRaw, country_code)
    if (!s.ok) return { valid: false, error: `Start: ${s.error}` }
    start_serial = s.serial
    start_serial_num = s.serialNum
  }

  if (!end_serial) {
    const e = parseSerial(endRaw, country_code)
    if (!e.ok) return { valid: false, error: `End: ${e.error}` }
    end_serial = e.serial
    end_serial_num = e.serialNum
  }

  if (start_serial_num > end_serial_num) {
    return { valid: false, error: 'Start serial must be less than or equal to end serial' }
  }

  const expected_count = end_serial_num - start_serial_num + 1

  return {
    valid: true,
    preview: {
      country_code,
      month_year,
      start_serial,
      end_serial,
      start_rfid_code: buildRfidCode(country_code, month_year, start_serial),
      end_rfid_code: buildRfidCode(country_code, month_year, end_serial),
      expected_count,
      needsCountryMonth,
    },
  }
}

export function formatCount(n: number): string {
  return n.toLocaleString()
}

export function currentMonthYear(): string {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear() % 100).padStart(2, '0')
  return `${mm}${yy}`
}

export function birthRangeRequestBody(preview: BirthRangePreview): {
  start_rfid_code: string
  end_rfid_code: string
} {
  return {
    start_rfid_code: preview.start_rfid_code,
    end_rfid_code: preview.end_rfid_code,
  }
}
